<template>
    <div class="action-dropdown" v-if="invoice.teamMemberInvoiceId">
        <div>
            <!-- Approve button (Draft only, not OnHold) -->
            <md-button
                v-if="invoice.status === 'Draft'"
                @click="approve"
                class="btn-green md-just-icon md-round"
                title="Approve Invoice"
            >
                <md-icon>thumb_up</md-icon>
            </md-button>
            <!-- Sync button (non-Draft, non-OnHold) -->
            <template v-else-if="invoice.status !== 'OnHold'">
                <div class="sync-loader md-button" v-if="isSyncing" :title="invoice.retryTooltip">
                    <straight-line-loader />
                </div>
                <md-button v-else @click="sync" :class="buttonColor" class="md-just-icon md-round" :title="buttonTitle">
                    <md-icon>{{ buttonIcon }}</md-icon>
                </md-button>
            </template>
            <!-- OnHold indicator with tooltip -->
            <md-button
                v-else
                class="btn-orange md-just-icon md-round"
                title="Invoice On Hold"
            >
                <md-icon>pause_circle</md-icon>
                <md-tooltip md-direction="left">
                    <span v-html="onHoldToolTip"></span>
                </md-tooltip>
            </md-button>

            <!-- Preview PDF -->
            <md-button @click="previewPdf" class="btn-blue md-just-icon md-round" title="Preview PDF">
                <md-icon>preview</md-icon>
            </md-button>

            <!-- Download Excel -->
            <md-button @click="generateExcel" class="btn-blue md-just-icon md-round" title="Download Excel">
                <md-icon>download</md-icon>
            </md-button>

            <!-- Send Email button (Draft or Regular) -->
            <template v-if="invoice.status === 'Draft'">
                <md-button
                    @click="sendEmail"
                    :class="draftButtonColor"
                    class="md-just-icon md-round"
                    :title="draftIconTitle"
                >
                    <md-icon>{{ draftButtonIcon }}</md-icon>
                    <md-tooltip v-if="draftEmailToolTip" md-direction="left">
                        {{ draftEmailToolTip }}
                    </md-tooltip>
                </md-button>
            </template>
            <template v-else>
                <template v-if="!hasFeatureTeamMemberInvoiceDraftApproval">
                    <md-button
                        @click="sendEmail"
                        :class="invoice.lastEmailDateSent ? 'btn-green' : 'btn-blue'"
                        class="md-just-icon md-round"
                        :title="emailButtonTitle"
                    >
                        <md-icon>{{ invoice.lastEmailDateSent ? 'mark_email_read' : 'email' }}</md-icon>
                        <md-tooltip v-if="invoice.lastEmailDateSent" md-direction="left">
                            Sent on: {{ invoice.lastEmailDateSent | dateTimeFormat(DATE_TYPES.standardDate) }}
                        </md-tooltip>
                    </md-button>
                </template>
                <template v-if="hasFeatureTeamMemberInvoiceDraftApproval && invoice.teamMemberShouldApplyTax">
                    <md-button
                        @click="sendEmail"
                        :class="invoice.lastEmailDateSent ? 'btn-green' : 'btn-blue'"
                        class="md-just-icon md-round"
                        :title="emailButtonTitle"
                    >
                        <md-icon>{{ invoice.lastEmailDateSent ? 'mark_email_read' : 'email' }}</md-icon>
                        <md-tooltip v-if="invoice.lastEmailDateSent" md-direction="left">
                            Sent on: {{ invoice.lastEmailDateSent | dateTimeFormat(DATE_TYPES.standardDate) }}
                        </md-tooltip>
                    </md-button>
                </template>
            </template>

            <!-- Delete -->
            <md-button @click="remove" class="btn-red md-just-icon md-round" title="Delete Invoice">
                <md-icon>delete</md-icon>
            </md-button>
        </div>
    </div>
</template>
<script>
import moment from 'moment';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage, openFile, downloadFile } from '@/helpers';
import { StraightLineLoader } from '@/components';
import { DATE_TYPES, FEATURE_NAMES } from '@/utils/constants';
import { mapGetters } from 'vuex';
import { FeatureManager } from '@/directives';
import { InvoiceErrorMessageModal } from '../../TeamMemberInvoices/components';

export default {
    name: 'ActionRendererTeamMemberInvoice',
    components: {
        StraightLineLoader
    },
    mixins: [GeneralMixin, FeatureManager],
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser'
        }),
        invoice() {
            return this.params || {};
        },
        isSyncing() {
            return Boolean(this.invoice.isSyncing);
        },
        // Sync button computed properties
        buttonColor() {
            if (this.invoice.accountingSystemRef != null) {
                return 'btn-green';
            }
            if (this.invoice.accountingSystemNote != null && this.invoice.accountingSystemNote !== 'OK') {
                return 'btn-red';
            }
            return 'btn-blue';
        },
        buttonTitle() {
            if (this.invoice.accountingSystemRef != null) {
                return `Invoice has successfully been sent to ${this.invoice.accountingSystemName}`;
            }
            if (this.invoice.accountingSystemNote != null && this.invoice.accountingSystemNote !== 'OK') {
                return `Error with syncing ${this.invoice.accountingSystemName}`;
            }
            return `Send invoice to accounting package`;
        },
        buttonIcon() {
            if (this.invoice.accountingSystemRef != null) {
                return 'done';
            }
            if (this.invoice.accountingSystemNote != null && this.invoice.accountingSystemNote !== 'OK') {
                return 'priority_high';
            }
            return 'send';
        },
        // Draft email button computed properties
        draftButtonIcon() {
            if (this.invoice.newDraftCostAdded && this.invoice.draftEmailHasBeenSent) {
                return 'mark_email_unread';
            }
            if (this.invoice.lastDraftEmailDateSent) {
                return 'mark_email_read';
            }
            return 'email';
        },
        draftIconTitle() {
            if (this.invoice.newDraftCostAdded) {
                return 'New cost added';
            }
            if (this.invoice.draftEmailHasBeenSent) {
                return `Email Sent`;
            }
            return 'Send Email';
        },
        draftButtonColor() {
            if (this.invoice.newDraftCostAdded) {
                return 'btn-yellow';
            }
            if (this.invoice.lastDraftEmailDateSent) {
                return 'btn-green';
            }
            return 'btn-blue';
        },
        draftEmailToolTip() {
            if (this.invoice.lastDraftEmailDateSent == null)
                return null;

            const emailSent = moment(this.invoice.lastDraftEmailDateSent).format(DATE_TYPES.standardDateTime);
            if (this.invoice.draftEmailHasBeenSent || this.invoice.newDraftCostAdded) {
                const sendAgain = this.invoice.newDraftCostAdded ? 'Re-Send updated invoice.' : '';
                return `${sendAgain} Last email Sent at ${emailSent}`;
            }
            return `Last email Sent at ${emailSent}`;
        },
        // Regular email button
        emailButtonTitle() {
            return this.invoice.lastEmailDateSent ? 'Email sent' : 'Send Email';
        },
        hasFeatureTeamMemberInvoiceDraftApproval() {
            return this.$_hasFeature(FEATURE_NAMES.TeamMemberInvoiceDraftApproval) && this.$_hasFeature(FEATURE_NAMES.DraftInvoices);
        },
        // OnHold tooltip
        onHoldToolTip() {
            const until = this.invoice.onHoldUntilDate
                ? `<b>${moment(this.invoice.onHoldUntilDate)
                    .add(1, 'days')
                    .format('MMM D, YYYY')}</b>`
                : '<b>indefinitely</b>';

            return `Team member is on hold during this date range. Invoice cannot be approved or emailed until ${until}.`;
        }
    },
    methods: {
        // Sync/Send to Accounting
        async sync() {
            if (this.invoice.accountingSystemRef != null) {
                return; // Already synced
            }
            if (this.invoice.accountingSystemNote != null && this.invoice.accountingSystemNote !== 'OK') {
                this.showSyncError();
                return;
            }
            await this.handleSync();
        },
        async handleSync() {
            this.updateSyncingState(true);
            try {
                const endpoint = `/api/team-member-invoices/send-to-accounting`;
                const data = { invoiceIdList: [this.invoice.teamMemberInvoiceId] };
                await handleRequests(endpoint, { method: 'post', data });
            } catch (error) {
                this.updateSyncingState(false);
                showErrorMessage(this, 'Error syncing invoice to accounting', error);
            }
        },
        showSyncError() {
            this.$modal.show(InvoiceErrorMessageModal, { invoice: this.invoice });
        },
        // Approve Invoice
        async approve() {
            if (this.isReadOnlyUser)
                return;
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Approve Invoice',
                    body: 'Are you sure you want to approve this invoice?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.handleApproveInvoice();
                    }
                });
        },
        async handleApproveInvoice() {
            this.$_handleLoaderState(true, 'APPROVING...');
            try {
                const api = `/api/team-member-invoices/${this.invoice.teamMemberInvoiceId}/approve`;
                await handleRequests(api, { method: 'post' });
                this.$notifySuccess('Invoice was approved!');
                this.params.refreshServerSide();
            } catch (e) {
                showErrorMessage(this, 'Error in approving the invoice.', e);
            }
            this.$_handleLoaderState(false);
        },
        // Preview PDF
        async previewPdf() {
            this.$_handleLoaderState(true, 'GENERATING...');
            try {
                const api = `/api/team-member-invoices/${this.invoice.teamMemberInvoiceId}/false/download`;
                const response = await handleRequests(api, { method: 'get', responseType: 'blob' });
                await openFile(response.data, 'invoice.pdf');
                this.$notifySuccess('Successfully generated a PDF');
            } catch (e) {
                showErrorMessage(this, 'Error in generating invoice.', e);
            }
            this.$_handleLoaderState(false);
        },
        // Download Excel
        async generateExcel() {
            this.$_handleLoaderState(true, 'GENERATING...');
            try {
                const api = `/api/team-member-invoices/${this.invoice.teamMemberInvoiceId}/download/excel`;
                const response = await handleRequests(api, { method: 'get', responseType: 'blob' });
                await downloadFile(response.data, 'invoiceData.xlsx');
                this.$notifySuccess('Successfully downloaded the invoice data');
            } catch (e) {
                showErrorMessage(this, 'Error in downloading invoice data.', e);
            }
            this.$_handleLoaderState(false);
        },
        // Send Email (Draft)
        async sendEmail() {
            if (this.invoice.status === 'Draft') {
                await this.handleSendDraft();
            } else {
                await this.handleSendRegularEmail();
            }
        },
        async handleSendDraft() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Email Invoice',
                    body: 'Are you sure you want to send draft approvals?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        try {
                            const api = `/api/team-member-invoices/${this.invoice.teamMemberInvoiceId}/send-draft`;
                            await handleRequests(api, { method: 'post' });
                            this.params.refreshServerSide();
                        } catch (e) {
                            showErrorMessage(this, 'Error in sending draft payment summaries', e);
                        }
                    }
                });
        },
        async handleSendRegularEmail() {
            this.$_handleLoaderState(true, 'SENDING...');
            try {
                const api = `/api/team-member-invoices/${this.invoice.teamMemberInvoiceId}/send-email`;
                await handleRequests(api, { method: 'post' });
                this.$notifySuccess('Invoice sent successfully');
                this.params.refreshServerSide();
            } catch (e) {
                showErrorMessage(this, 'Error in sending invoice.', e);
            }
            this.$_handleLoaderState(false);
        },
        // Delete Invoice
        async remove() {
            if (this.isReadOnlyUser)
                return;
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Delete Invoice',
                    body: 'Are you sure you want to delete this invoice?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.handleDeleteInvoice();
                    }
                });
        },
        async handleDeleteInvoice() {
            this.$_handleLoaderState(true, 'DELETING...');
            try {
                const api = `/api/team-member-invoices/${this.invoice.teamMemberInvoiceId}`;
                await handleRequests(api, { method: 'delete' });
                this.$notifySuccess('Invoice was deleted!');
                this.params.refreshServerSide();
            } catch (e) {
                showErrorMessage(this, 'Error in deleting the invoice.', e);
            }
            this.$_handleLoaderState(false);
        },
        updateSyncingState(value) {
            const invoiceData = this.params?.node?.data;
            if (!invoiceData)
                return;

            if (invoiceData.isSyncing === undefined) {
                this.$set(invoiceData, 'isSyncing', value);
            } else {
                invoiceData.isSyncing = value;
            }

            if (this.params?.api && this.params?.node) {
                this.params.api.refreshCells({ rowNodes: [this.params.node], force: true });
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.action-dropdown {
    ::v-deep .md-button {
        width: 30px;
        height: 30px;
        vertical-align: middle;
        min-width: 30px;

        .md-button-content i {
            color: black !important;
        }
    }

    ::v-deep .md-ripple {
        padding: 10px !important;
    }
}

.action-dropdown > div {
    display: inline-flex;
    align-items: center;

    .md-button {
        margin-right: 5px;
    }

    .btn-blue {
        background-color: #2b93ff !important;
        border: 1px solid #2b93ff !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-green {
        background-color: #1db51d !important;
        border: 1px solid #1db51d !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-red {
        background-color: #ff0600 !important;
        border: 1px solid #ff0600 !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-yellow {
        background-color: #ffb400 !important;
        border: 1px solid #ffb400 !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-orange {
        background-color: #ff9800 !important;
        border: 1px solid #ff9800 !important;
        cursor: default;

        .md-button-content i {
            color: #fff !important;
        }
    }
}

.email-loader {
    width: 30px;
    height: 30px;
    margin-right: 5px;
    display: inline-block;
    background-color: #2b93ff;
    border-radius: 50%;
}

.sync-loader {
    display: inline-block;
    background-color: #2b93ff !important;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    svg {
        margin-top: 4px;
        margin-left: 3px;
    }
}
</style>
